import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { getProjectStatusFilterKey } from "../../common/header/settings/projects/projectFormMeta";
import { fetchTimesheetProjects, mapTimesheetProjectsResponse } from "./timesheetApi";

const { Project } = config.REST_API;

/** Statuses that typically allow time logging (aligned with backend timesheet project rules). */
const TIMELOG_ELIGIBLE_STATUSES = new Set(["active", "planning", "review", "completed", "inProgress"]);

/**
 * @typedef {"no_projects" | "inactive_or_on_hold" | "not_assigned" | "ok"} TimelogProjectSituationKind
 * @typedef {{ kind: TimelogProjectSituationKind, totalProjects?: number, blockedCount?: number }} TimelogProjectSituation
 */

async function fetchCompanyProjectsSample(max = 200) {
  try {
    const res = await axiosBasic.get(`${Project.Get}0/1/${max}`);
    const body = res.data;
    if (body?.success === false) return [];
    const list = body?.data ?? body?.Data;
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

/**
 * Determines why the user cannot log time against projects on `/timesheets/time-log/new`.
 * @returns {Promise<TimelogProjectSituation>}
 */
export async function resolveTimelogProjectSituation() {
  const [timesheetRaw, companyProjects] = await Promise.all([
    fetchTimesheetProjects().catch(() => null),
    fetchCompanyProjectsSample(),
  ]);

  const eligibleAssigned = mapTimesheetProjectsResponse(timesheetRaw);
  if (eligibleAssigned.length > 0) {
    return { kind: "ok" };
  }

  if (companyProjects.length === 0) {
    return { kind: "no_projects", totalProjects: 0 };
  }

  const activeRecords = companyProjects.filter((p) => p.isActive !== false && p.IsActive !== false);
  if (activeRecords.length === 0) {
    return { kind: "inactive_or_on_hold", totalProjects: companyProjects.length, blockedCount: companyProjects.length };
  }

  const statusKeys = activeRecords.map((p) => getProjectStatusFilterKey(p));
  const hasEligible = statusKeys.some((s) => TIMELOG_ELIGIBLE_STATUSES.has(s));

  if (!hasEligible) {
    const blockedCount = statusKeys.filter((s) => s === "onHold" || s === "inactive" || s === "deactivated").length;
    return {
      kind: "inactive_or_on_hold",
      totalProjects: companyProjects.length,
      blockedCount: blockedCount || activeRecords.length,
    };
  }

  return { kind: "not_assigned", totalProjects: companyProjects.length };
}
